import {
  Badge,
  Button,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Spinner,
  Switch,
  Textarea,
} from '@repo/ui'
import { useQueryClient } from '@tanstack/react-query'
import { Plus, Save, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { DurationInput } from '@/components/common/duration-input'
import { MessageComposer } from '@/components/common/message-composer'
import { useAuth } from '@/hooks/use-auth'
import {
  type FollowUpInactivityType,
  type FollowUpScope,
  type FollowUpStep,
  useCreateFollowUpConfig,
  useFollowUpConfig,
  useFollowUpConfigs,
  useUpdateFollowUpConfig,
} from '@/hooks/use-follow-ups'
import { useKanbanStages, useTags } from '@/hooks/use-leads'
import {
  META_TEMPLATE_VARIABLE_OPTIONS,
  useSyncedMetaTemplates,
} from '@/hooks/use-meta-templates'
import { useCustomFields } from '@/hooks/use-settings'
import { api } from '@/lib/api'
import { customFieldsToVariables } from '@/lib/message-variables'
import { PRO_LIMITS } from '@/types/billing'

const LEGACY_STAGE_NAMES: Record<string, string> = {
  new: 'Novo',
  contacted: 'Contactado',
  qualified: 'Qualificado',
  proposal: 'Proposta',
  won: 'Ganho',
  lost: 'Perdido',
  LEAD: 'Novo',
  QUALIFIED: 'Qualificado',
  CUSTOMER: 'Ganho',
  INACTIVE: 'Perdido',
}

const SCOPE_LABEL: Record<FollowUpScope, string> = {
  AI_ATTENDANCES: 'Atendimentos com IA ativa',
  HUMAN_ATTENDANCES: 'Atendimentos com humano',
  ALL: 'Todos os atendimentos',
}

const INACTIVITY_TYPE_LABEL: Record<FollowUpInactivityType, string> = {
  WAITING_CONTACT_REPLY: 'Lead não respondeu (mensagem ao lead)',
  WAITING_TEAM_REPLY: 'Equipe não respondeu (alerta interno)',
}

type DraftStep = Omit<FollowUpStep, 'id' | 'configId'> & { _id?: string }

interface FollowUpTabProps {
  agentId: string
  agentName: string
}

export function FollowUpTab({ agentId, agentName }: FollowUpTabProps) {
  const { currentOrgId } = useAuth()
  const { data: config, isLoading } = useFollowUpConfig(currentOrgId, agentId)
  const { data: allConfigs = [] } = useFollowUpConfigs(currentOrgId)
  const { data: tags = [] } = useTags(currentOrgId)
  const { data: stages = [] } = useKanbanStages(currentOrgId)
  const { data: metaTemplates = [] } = useSyncedMetaTemplates(currentOrgId)
  const { data: customFields = [] } = useCustomFields(currentOrgId)
  const customVariables = useMemo(
    () => customFieldsToVariables(customFields),
    [customFields]
  )

  const qc = useQueryClient()
  const createConfig = useCreateFollowUpConfig(currentOrgId)
  const updateConfig = useUpdateFollowUpConfig(currentOrgId, config?.id ?? '')

  const [isActive, setIsActive] = useState(false)
  const [scope, setScope] = useState<FollowUpScope>('ALL')
  const [inactivityType, setInactivityType] = useState<FollowUpInactivityType>(
    'WAITING_CONTACT_REPLY'
  )
  const [inactivityMinutes, setInactivityMinutes] = useState(60)
  const [stopOnReply, setStopOnReply] = useState(true)
  const [stopStages, setStopStages] = useState<string[]>([
    'Ganho',
    'Perdido',
    'Fora de ICP',
  ])
  const [stopTagIds, setStopTagIds] = useState<Set<string>>(new Set())
  const [steps, setSteps] = useState<DraftStep[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (config === undefined) return
    if (config === null) {
      setIsActive(false)
      setScope('ALL')
      setInactivityType('WAITING_CONTACT_REPLY')
      setInactivityMinutes(60)
      setStopOnReply(true)
      setStopStages(['Ganho', 'Perdido', 'Fora de ICP'])
      setStopTagIds(new Set())
      setSteps([])
      return
    }
    setIsActive(config.isActive)
    setScope(config.scope ?? 'ALL')
    setInactivityType(config.inactivityType ?? 'WAITING_CONTACT_REPLY')
    setInactivityMinutes(config.inactivity_minutes)
    setStopOnReply(config.stop_on_reply)
    setStopStages(
      (config.stopOnStages ?? []).map(
        (stage) => LEGACY_STAGE_NAMES[stage] ?? stage
      )
    )
    setStopTagIds(new Set(config.stopOnTagIds ?? []))
    setSteps(
      (config.steps ?? []).map((s) => ({
        _id: s.id,
        position: s.position,
        mode: s.mode,
        content: s.content ?? '',
        metadata: s.metadata ?? null,
        delay_minutes: s.delay_minutes,
      }))
    )
  }, [config])

  const isTeamReply = inactivityType === 'WAITING_TEAM_REPLY'

  const usedConfigs = allConfigs.length
  const configLimit = PRO_LIMITS.followUpConfigs ?? Infinity
  const atLimit = !config && usedConfigs >= configLimit

  function addDraftStep() {
    if (steps.length >= 5) return
    setSteps((prev) => [
      ...prev,
      {
        position: prev.length,
        delay_minutes: 60,
        mode: 'TEXT',
        content: '',
        metadata: null,
      },
    ])
  }

  function patchStep(idx: number, patch: Partial<DraftStep>) {
    setSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))
  }

  function removeDraftStep(idx: number) {
    setSteps((prev) =>
      prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, position: i }))
    )
  }

  async function handleSave() {
    if (!currentOrgId) return
    setSaving(true)
    try {
      const configPayload = {
        name: `Follow-up: ${agentName}`,
        agentId,
        scope,
        inactivityType,
        inactivity_minutes: inactivityMinutes,
        stop_on_reply: stopOnReply,
        stopOnStages: stopStages,
        stopOnTagIds: Array.from(stopTagIds),
        isActive,
      }

      let configId = config?.id

      if (!configId) {
        const created = await createConfig.mutateAsync(configPayload)
        configId = created.id
      } else {
        await updateConfig.mutateAsync(configPayload)
      }

      if (!isTeamReply) {
        const serverIds = new Set(config?.steps.map((s) => s.id) ?? [])
        const keepIds = new Set(
          steps.map((s) => s._id).filter(Boolean) as string[]
        )

        for (const sid of serverIds) {
          if (!keepIds.has(sid)) {
            await api.delete(
              `/orgs/${currentOrgId}/follow-up-configs/${configId}/steps/${sid}`
            )
          }
        }

        for (const [idx, step] of steps.entries()) {
          const body = {
            position: idx,
            mode: step.mode,
            content: step.content,
            metadata: step.metadata ?? undefined,
            delay_minutes: step.delay_minutes,
          }
          if (step._id && serverIds.has(step._id)) {
            await api.put(
              `/orgs/${currentOrgId}/follow-up-configs/${configId}/steps/${step._id}`,
              body
            )
          } else {
            await api.post(
              `/orgs/${currentOrgId}/follow-up-configs/${configId}/steps`,
              body
            )
          }
        }
      }

      await qc.invalidateQueries({
        queryKey: ['follow-up-configs', currentOrgId],
      })
      toast.success('Follow-up salvo')
    } catch {
      toast.error('Erro ao salvar follow-up')
    } finally {
      setSaving(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">
          Configurações de follow-up usadas
        </span>
        <span
          className={
            usedConfigs >= configLimit
              ? 'text-destructive font-medium'
              : 'text-muted-foreground'
          }
        >
          {usedConfigs} / {configLimit}
        </span>
      </div>

      {atLimit ? (
        <div className="border-warning/30 bg-warning/10 text-warning rounded-md border p-4 text-sm">
          Limite de {configLimit} configurações de follow-up atingido. Remova
          uma configuração existente para criar uma nova.
        </div>
      ) : null}

      <section className="bg-card rounded-md border p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold">Sequência de follow-up</h2>
            <p className="text-muted-foreground mt-0.5 text-xs">
              Ações automáticas para atendimentos inativos.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              onCheckedChange={setIsActive}
              id="fu-active"
              disabled={atLimit}
            />
            <Label htmlFor="fu-active" className="cursor-pointer text-xs">
              {isActive ? 'Ativa' : 'Inativa'}
            </Label>
          </div>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Escopo</Label>
            <Select
              value={scope}
              onValueChange={(v) => setScope(v as FollowUpScope)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(SCOPE_LABEL) as FollowUpScope[]).map((key) => (
                  <SelectItem key={key} value={key}>
                    {SCOPE_LABEL[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs">Tipo de inatividade</Label>
            <Select
              value={inactivityType}
              onValueChange={(v) =>
                setInactivityType(v as FollowUpInactivityType)
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  Object.keys(INACTIVITY_TYPE_LABEL) as FollowUpInactivityType[]
                ).map((key) => (
                  <SelectItem key={key} value={key}>
                    {INACTIVITY_TYPE_LABEL[key]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isTeamReply ? (
              <p className="text-muted-foreground text-xs">
                Gera um alerta interno — nenhuma mensagem é enviada ao lead.
              </p>
            ) : null}
          </div>
        </div>

        <div className="mt-4">
          <Label className="text-xs">
            Inatividade mínima antes da 1ª etapa
          </Label>
          <p className="text-muted-foreground mb-1.5 text-xs">
            {isTeamReply
              ? 'Tempo sem resposta da equipe para disparar o alerta.'
              : 'Tempo desde a última mensagem sem resposta do lead para iniciar a sequência.'}
          </p>
          <DurationInput
            storeUnit="minutes"
            segments={['days', 'hours', 'minutes']}
            min={1}
            max={2880}
            value={inactivityMinutes}
            onChange={(v) => setInactivityMinutes(v ?? 60)}
          />
        </div>
      </section>

      <section className="bg-card rounded-md border p-5">
        <h2 className="mb-1 text-sm font-semibold">Critérios de parada</h2>
        <p className="text-muted-foreground mb-4 text-xs">
          A sequência interrompe quando qualquer condição for atendida.
        </p>

        <div className="bg-muted/30 flex items-center justify-between rounded-md border px-3 py-2">
          <div>
            <Label className="text-xs">Parar quando o lead responder</Label>
            <p className="text-muted-foreground text-xs">
              Recomendado. Evita follow-up após o lead voltar a conversar.
            </p>
          </div>
          <Switch checked={stopOnReply} onCheckedChange={setStopOnReply} />
        </div>

        <div className="mt-4">
          <Label className="text-xs">Parar quando estagio do lead for...</Label>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {stages.map((stage) => {
              const active = stopStages.includes(stage.name)
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() =>
                    setStopStages((prev) =>
                      prev.includes(stage.name)
                        ? prev.filter((name) => name !== stage.name)
                        : [...prev, stage.name]
                    )
                  }
                  className={`rounded-md border px-2 py-1 text-xs transition-colors ${
                    active
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card text-muted-foreground hover:bg-accent/30'
                  }`}
                >
                  {stage.name}
                </button>
              )
            })}
          </div>
        </div>

        {tags.length > 0 && (
          <div className="mt-4">
            <Label className="text-xs">Parar quando lead tiver tag...</Label>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {tags.map((t) => {
                const active = stopTagIds.has(t.id)
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      setStopTagIds((prev) => {
                        const n = new Set(prev)
                        if (n.has(t.id)) n.delete(t.id)
                        else n.add(t.id)
                        return n
                      })
                    }
                    className={`flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-card text-muted-foreground hover:bg-accent/30'
                    }`}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: t.color }}
                    />
                    {t.name}
                  </button>
                )
              })}
            </div>
          </div>
        )}
      </section>

      {!isTeamReply ? (
        <section className="bg-card rounded-md border p-5">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold">Etapas da sequência</h2>
              <p className="text-muted-foreground mt-0.5 text-xs">
                Até 5 mensagens. Cada uma envia após o atraso configurado.
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addDraftStep}
              disabled={steps.length >= 5}
            >
              <Plus className="h-3.5 w-3.5" />
              Adicionar etapa
            </Button>
          </div>

          {steps.length === 0 ? (
            <div className="text-muted-foreground rounded-md border border-dashed py-10 text-center text-xs">
              Nenhuma etapa configurada. Adicione a primeira mensagem de
              follow-up.
            </div>
          ) : (
            <div className="space-y-3">
              {steps.map((step, idx) => (
                <div key={idx} className="bg-background rounded-md border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      Etapa {idx + 1}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:bg-destructive/10 h-7 w-7"
                      onClick={() => removeDraftStep(idx)}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <Label className="text-xs">
                        Atraso após etapa anterior
                      </Label>
                      <div className="mt-2">
                        <DurationInput
                          storeUnit="minutes"
                          segments={['days', 'hours', 'minutes']}
                          min={1}
                          max={4320}
                          value={step.delay_minutes}
                          onChange={(v) =>
                            patchStep(idx, { delay_minutes: v ?? 60 })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Tipo de conteúdo</Label>
                      <Select
                        value={step.mode}
                        onValueChange={(v) =>
                          patchStep(idx, { mode: v as 'TEXT' | 'AI' })
                        }
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TEXT">
                            Texto fixo (com variaveis)
                          </SelectItem>
                          <SelectItem value="AI">
                            Prompt para IA gerar
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Label className="text-xs">Template oficial opcional</Label>
                    <Select
                      value={
                        typeof step.metadata?.meta_template_id === 'string'
                          ? step.metadata.meta_template_id
                          : 'FREE_TEXT'
                      }
                      onValueChange={(next) => {
                        const value = next === 'FREE_TEXT' ? '' : next
                        const template = metaTemplates.find(
                          (item) => item.id === value
                        )
                        patchStep(idx, {
                          content: template?.content ?? step.content,
                          metadata: value
                            ? {
                                ...(step.metadata ?? {}),
                                meta_template_id: value,
                                meta_template_variables:
                                  template?.variables.map(() => '') ?? [],
                              }
                            : null,
                        })
                      }}
                    >
                      <SelectTrigger className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="FREE_TEXT">
                          Usar texto livre dentro da janela
                        </SelectItem>
                        {metaTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name} ({template.language})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {Array.isArray(step.metadata?.meta_template_variables)
                    ? step.metadata.meta_template_variables.map(
                        (value, variableIndex) => (
                          <div className="mt-3" key={variableIndex}>
                            <Label className="text-xs">
                              Origem de{' '}
                              {metaTemplates.find(
                                (item) =>
                                  item.id === step.metadata?.meta_template_id
                              )?.variables[variableIndex] ??
                                `{{${variableIndex + 1}}}`}
                            </Label>
                            <Select
                              value={
                                typeof value === 'string' && value
                                  ? value
                                  : undefined
                              }
                              onValueChange={(next) =>
                                patchStep(idx, {
                                  metadata: {
                                    ...(step.metadata ?? {}),
                                    meta_template_variables: (
                                      step.metadata
                                        ?.meta_template_variables as unknown[]
                                    ).map((item, index) =>
                                      index === variableIndex ? next : item
                                    ),
                                  },
                                })
                              }
                            >
                              <SelectTrigger className="mt-1 w-full">
                                <SelectValue placeholder="Selecione o campo do lead" />
                              </SelectTrigger>
                              <SelectContent>
                                {META_TEMPLATE_VARIABLE_OPTIONS.map(
                                  (option) => (
                                    <SelectItem
                                      key={option.value}
                                      value={option.value}
                                    >
                                      {option.label}
                                    </SelectItem>
                                  )
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        )
                      )
                    : null}

                  <div className="mt-3">
                    <Label className="text-xs">
                      {step.mode === 'TEXT'
                        ? 'Mensagem'
                        : 'Instrução para a IA'}
                    </Label>
                    {step.mode === 'TEXT' ? (
                      <div className="mt-1">
                        <MessageComposer
                          value={step.content ?? ''}
                          onChange={(v) => patchStep(idx, { content: v })}
                          rows={3}
                          placeholder="Ex.: {{saudacao}}, {{nome}}! Passando para ver se tem dúvidas."
                          groups={['lead', 'responsavel_org', 'saudacao']}
                          customVariables={customVariables}
                        />
                      </div>
                    ) : (
                      <Textarea
                        rows={3}
                        value={step.content ?? ''}
                        onChange={(e) =>
                          patchStep(idx, { content: e.target.value })
                        }
                        placeholder="Ex.: Pergunte se ele teve dúvidas sobre a proposta e sugira uma call."
                        className="mt-1"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ) : (
        <section className="bg-muted/30 rounded-md border p-5 text-sm">
          <p className="font-medium">Alerta interno</p>
          <p className="text-muted-foreground mt-1 text-xs">
            Com o tipo <strong>Equipe não respondeu</strong>, nenhuma mensagem é
            enviada ao lead. O sistema gera um alerta interno para que a equipe
            tome ação.
          </p>
        </section>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={saving || atLimit}
        >
          {saving ? <Spinner /> : <Save className="h-4 w-4" />}
          Salvar follow-up
        </Button>
      </div>
    </div>
  )
}
